<template>
    <div>
        <vrm-header header-class="vrm-header__fluid">
            <template v-slot:header-controls>
                <div class="vrm-tasks">
                    <div class="vrm-tasks__task">
                        <vrm-toggle-button
                            v-model="filterBar"
                            label-class="gtag-top-menu-item gtag-top-menu-item--tags"
                            focus="headerColumns"
                            icon-class="sg-vrm-icon_140_filter-16px"
                        ></vrm-toggle-button>
                    </div>
                </div>
            </template>
        </vrm-header>
        <vrm-title :panels-under-header="true" header-class="vrm-header__fluid">
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span>
                        {{ $t('nav.release_overview', { i18nFallback: 'Release overview' }) }}
                    </span>
                </h1>
            </template>

            <template v-slot:header-panels>
                <div id="header-tags">
                    <div
                        id="headerTags"
                        tabindex="0"
                        class="vrm-togglable-panel vrm-togglable-panel--inside--outside-header"
                        :class="{
                            'vrm-open vrm-togglable-panel--tags vrm-open-overflow': filterBar,
                        }"
                    >
                        <div class="container">
                            <div class="row">
                                <div class="col-xs-12">
                                    <vrm-filter-bar
                                        v-if="filterBar"
                                        :dropdown-filter="filters"
                                        @clear-filter-bar-values="clearFilterBarValues"
                                        @apply-filter="applyFilter"
                                        @done-filter="doneFilter"
                                    ></vrm-filter-bar>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template v-slot:header-controls>
                <div class="vrm-tasks">
                    <div class="vrm-tasks__task">
                        <vrm-toggle-button
                            v-model="filterBar"
                            label-class="gtag-top-menu-item gtag-top-menu-item--tags"
                            focus="headerColumns"
                            icon-class="sg-vrm-icon_140_filter-16px"
                        ></vrm-toggle-button>
                    </div>
                </div>
            </template>
        </vrm-title>
        <div class="container container__fluid">
            <p v-if="loading" class="sg-color-blue">
                {{ $t('core.loading', { i18nFallback: 'Loading...' }) }}
            </p>

            <p v-else-if="!releases.length" class="sg-color-blue">
                {{
                    $t('firmware-library.noReleases', {
                        i18nFallback: 'There are no releases matching your criteria',
                    })
                }}
            </p>
            <vrm-scroll-fade>
                <vrm-foldable-table
                    :sort-compare-function="sortCompareFunction"
                    :items="releases"
                    key-by="idRelease"
                >
                    <template v-slot:thead>
                        <tr>
                            <th
                                v-for="column in columns"
                                :key="String(column.key)"
                                class="vrm-table__sort"
                                :class="{
                                    'vrm-table__sort--descending':
                                        sortBy.key === column.key && sortBy.order === 'descending',
                                    'vrm-table__sort--ascending':
                                        sortBy.key === column.key && sortBy.order === 'ascending',
                                }"
                                @click="toggleSort(column.key)"
                            >
                                {{ column.formatted }}
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: release, toggle, expanded }">
                        <template v-for="column in columns">
                            <vrm-table-cell-url
                                v-if="column.key === 'title'"
                                :key="column.key + '_item' + release.idRelease"
                                :text="String(release[column.key])"
                                :url="`/edit-release/${release.idRelease}`"
                            ></vrm-table-cell-url>
                            <vrm-table-cell-text
                                v-else
                                :key="column.key + '_item' + release.idRelease"
                                :text="
                                    column.key === 'createdAt'
                                        ? moment(release[column.key] * 1000).format(
                                              'D MMM YYYY HH:mm',
                                          )
                                        : String(release[column.key])
                                "
                            ></vrm-table-cell-text>
                        </template>
                        <td class="vrm-table__actions">
                            <a
                                class="vrm-table__icon--link"
                                :class="
                                    expanded
                                        ? 'sg-vrm-icon_059_min-round-16px'
                                        : 'sg-vrm-icon_058_plus-round-16px'
                                "
                                @click="toggle"
                            />
                        </td>
                    </template>
                    <template v-slot:extra="{ item: release }">
                        <td colspan="999">
                            <vrm-foldable-table :items="release.firmwares" key-by="idLibrary">
                                <template v-slot:thead>
                                    <th v-for="column in fileColumns" :key="String(column.key)">
                                        {{ column.formatted }}
                                    </th>
                                </template>
                                <template v-slot:main="{ item: firmware }">
                                    <template v-for="column in fileColumns">
                                        <template v-if="column.key === 'downloadUrl'">
                                            <vrm-table-cell-file
                                                :key="column.key + '_item' + firmware.idLibrary"
                                                :text="
                                                    $t('firmware-library.downloadFirmware', {
                                                        i18nFallback: 'Download firmware',
                                                    })
                                                "
                                                :download-url="firmware.downloadUrl"
                                            ></vrm-table-cell-file>
                                        </template>
                                        <vrm-table-cell-text
                                            v-else
                                            :key="column.key + '_item' + firmware.idLibrary"
                                            :text="
                                                Array.isArray(firmware[column.key])
                                                    ? firmware[column.key].join(', ')
                                                    : String(firmware[column.key])
                                            "
                                        ></vrm-table-cell-text>
                                    </template>
                                </template>
                            </vrm-foldable-table>

                            <div class="sg-spread"></div>
                        </td>
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>

            <div class="sg-btn__container sg-btn__container--align-right">
                <a href="/create-new-release" class="sg-btn sg-blue">
                    {{
                        $t('nav.create_new_release', {
                            i18nFallback: 'Create new release',
                        })
                    }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmHeader from '@/vue/components/layout/VrmHeader.vue';
import VrmTitle from '@/vue/components/layout/VrmTitle.vue';
import VrmToggleButton from '@/vue/components/shared/toggles/VrmToggleButton.vue';
import VrmScrollFade from '../../shared/VrmScrollFade.vue';
import VrmTableCellFile from '@/vue/components/table/VrmTableCellFile.vue';
import VrmTableCellText from '@/vue/components/table/VrmTableCellText.vue';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable.vue';
import VrmFilterBar from '@/vue/components/shared/VrmFilterBar.vue';
import moment from 'moment';
import VrmTableCellUrl from '@/vue/components/table/VrmTableCellUrl.vue';
import {
    addDropdownFilterToUrlParams,
    sortCompareFunction,
    updateUrlSearch,
} from '@/vue/components/pages/firmware-library/utils';

@Component({
    components: {
        VrmFoldableTable,
        VrmTableCellText,
        VrmTableCellFile,
        VrmTableCellUrl,
        VrmToggleButton,
        VrmHeader,
        VrmScrollFade,
        VrmTitle,
        VrmFilterBar,
    },
})
@AngularRoute('base.release-overview', '/release-overview', { slot: 'main' })
@AngularLocaleBundle('nav')
@AngularLocaleBundle('tags')
@AngularLocaleBundle('firmware-library')
export default class VrmReleaseOverview extends Vue {
    /** @type FirmwareLibraryService */
    @ngInject()
    FirmwareLibraryService;

    loading = false;
    moment = moment;

    filterBar = false;
    filters = [];
    filterParams = {};

    initializers = {
        productIds: this.getProductIdsFilter,
        title: this.getTitleFilter,
        minVCVersion: this.getMinimumVCVersionFilter,
        firmwareVersion: this.getFirmwareVersionFilter,
        minVersion: this.getMinimumFirmwareVersionFilter,
        minVersionSettings: this.getMinimumSettingsVersionFilter,
        createdBy: this.getCreatedByFilter,
    };

    sortBy = { key: null, order: 'ascending' };

    toggleSort(key) {
        if (this.sortBy.key !== key) {
            this.sortBy.key = key;
            this.sortBy.order = 'ascending';
        } else if (this.sortBy.order === 'ascending') {
            this.sortBy.order = 'descending';
        } else {
            this.sortBy.key = null;
        }
    }

    get sortCompareFunction() {
        return sortCompareFunction.call(this);
    }

    get releases() {
        return this.$store.state.FirmwareLibraryService.releases;
    }

    get unfilteredReleases() {
        return this.$store.state.FirmwareLibraryService.initReleases;
    }

    get deviceNames() {
        return this.$store.state.FirmwareLibraryService.deviceNames;
    }

    get columns() {
        return this.$store.state.FirmwareLibraryService.releaseColumns;
    }

    get fileColumns() {
        return this.$store.state.FirmwareLibraryService.releaseFileColumns;
    }

    async created() {
        await this.FirmwareLibraryService.reloadReleases().then(() => {
            this.initializeFilters();
            const newParams = new URLSearchParams(window.location.search);
            if (newParams.size) this.onFiltersChanged(new URLSearchParams(window.location.search));
        });
    }

    initializeFilters(filterId) {
        if (filterId) {
            this.filters = this.filters.map(filter =>
                filter.id === filterId ? this.initializers[filterId]() : filter,
            );
            return;
        }
        this.filters = [];
        Object.values(this.initializers).forEach(value => this.filters.push(value()));
    }

    clearFilterBarValues(filterId) {
        if (filterId) {
            this.filterParams[filterId] = null;
        } else {
            this.filterParams = {};
        }
        this.initializeFilters(filterId);
        updateUrlSearch.call(this);
    }

    applyFilter() {
        addDropdownFilterToUrlParams.call(this);
        updateUrlSearch.call(this);
    }

    async onFiltersChanged(newValue) {
        this.FirmwareLibraryService.filterReleases(newValue);
        this.filters.forEach(filter => {
            let paramValue = newValue.get(filter.id);
            if (!paramValue) return;

            filter.options.forEach(filterOption => {
                if (filterOption.type === 'tags' || filterOption.type === 'tagSearch') {
                    paramValue = paramValue.split(',');
                    filterOption.options.forEach(tag => (tag.value = paramValue.includes(tag.id)));
                } else if (filterOption.type === 'textSearch') {
                    filterOption.value = paramValue ? paramValue : '';
                } else if (
                    filterOption.type === 'minMaxString' ||
                    filterOption.type === 'minMaxCombo'
                ) {
                    paramValue = paramValue.split(',');
                    filterOption.min = paramValue[0] ? paramValue[0] : '';
                    filterOption.max = paramValue[1] ? paramValue[1] : '';
                }
            });
            newValue.get(filter.id);
        });
    }

    doneFilter() {
        addDropdownFilterToUrlParams.call(this);
        updateUrlSearch.call(this);
        this.filterBar = false;
    }

    getProductIdsFilter() {
        const productIds = Array.from(
            new Set(
                this.unfilteredReleases.flatMap(release =>
                    release.firmwares.flatMap(firmware => firmware.idProductArray),
                ),
            ),
        ).sort();
        return {
            id: 'productIds',
            label: 'Product ID',
            i18n: 'firmware-library.productId',
            options: [
                {
                    type: 'tagSearch',
                    variable: 'productIds',
                    options: productIds.map(id => {
                        const name = this.deviceNames[
                            (id + '').replace(/^0x/i, '').replace(/^0+/, '')
                        ];
                        return {
                            id: id,
                            label: id + ' - ' + name,
                            value: false,
                            variable: id,
                        };
                    }),
                },
            ],
        };
    }

    getCreatedByFilter() {
        const createdBy = Array.from(
            new Set(this.unfilteredReleases.flatMap(release => release.createdByName)),
        ).sort();
        return {
            id: 'createdBy',
            label: 'Created by',
            i18n: 'firmware-library.createdBy',
            options: [
                {
                    type: 'tags',
                    variable: 'createdBy',
                    options: createdBy.map(id => {
                        return {
                            id: id,
                            label: id,
                            value: false,
                            variable: id,
                        };
                    }),
                },
            ],
        };
    }

    getFirmwareVersionFilter() {
        const firmwareVersions = Array.from(
            new Set(
                this.unfilteredReleases.flatMap(release =>
                    release.firmwares.flatMap(firmware => firmware.firmwareVersion),
                ),
            ),
        ).sort();
        return {
            i18n: 'firmware-library.firmwareVersion',
            label: 'Firmware version',
            id: 'firmwareVersion',
            options: [
                {
                    type: 'minMaxCombo',
                    i18n: 'firmware-library.firmwareVersion',
                    i18nFallback: 'Firmware version',
                    label: 'Firmware version',
                    min: '',
                    max: '',
                    options: firmwareVersions,
                },
            ],
        };
    }

    getMinimumVCVersionFilter() {
        const victronConnectVersions = Array.from(
            new Set(this.unfilteredReleases.flatMap(release => release.minVictronConnectVersion)),
        ).sort();
        return {
            i18n: 'firmware-library.minVictronConnectVersion',
            label: 'Minimum VictronConnect version',
            id: 'minVCVersion',
            options: [
                {
                    type: 'minMaxCombo',
                    i18n: 'firmware-library.minVictronConnectVersion',
                    i18nFallback: 'Minimum VictronConnect version',
                    label: 'Minimum VictronConnect version',
                    min: '',
                    max: '',
                    options: victronConnectVersions,
                },
            ],
        };
    }

    getMinimumFirmwareVersionFilter() {
        const minFirmwareVersions = Array.from(
            new Set(this.unfilteredReleases.flatMap(release => release.minVersion)),
        ).sort();
        return {
            i18n: 'firmware-library.minVersion',
            label: 'Minimum firmware version',
            id: 'minVersion',
            options: [
                {
                    type: 'minMaxCombo',
                    i18n: 'firmware-library.minVersion',
                    i18nFallback: 'Minimum firmware version',
                    label: 'Minimum firmware version',
                    min: '',
                    max: '',
                    options: minFirmwareVersions,
                },
            ],
        };
    }

    getMinimumSettingsVersionFilter() {
        const minSettingsVersions = Array.from(
            new Set(this.unfilteredReleases.flatMap(release => release.minVersionSettings)),
        ).sort();
        return {
            i18n: 'firmware-library.minVersionSettings',
            label: 'Minimum setting required version',
            id: 'minVersionSettings',
            options: [
                {
                    type: 'minMaxCombo',
                    i18n: 'firmware-library.minVersionSettings',
                    i18nFallback: 'Minimum setting required version',
                    label: 'Minimum setting required version',
                    min: '',
                    max: '',
                    options: minSettingsVersions,
                },
            ],
        };
    }

    getTitleFilter() {
        return {
            i18n: 'firmware-library.releaseTitleId',
            label: 'Release Title (or ID)',
            id: 'title',
            options: [
                {
                    type: 'textSearch',
                    i18n: 'firmware-library.releaseTitleId',
                    i18nFallback: 'Release Title (or ID)',
                    label: 'Release Title (or ID)',
                    value: '',
                },
            ],
        };
    }
}
</script>
